import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from './AdminLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import Toast from '../../components/common/Toast';

// ─── Constants ────────────────────────────────────────────────
const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api').replace(/\/api$/, '');

const VERIFY_STATUSES = [
  { value: 'verified',  label: 'Approve & Verify',  description: 'Business meets all requirements',   color: 'green', icon: '✓' },
  { value: 'pending',   label: 'Set to Pending',     description: 'Needs further review',             color: 'amber', icon: '◷' },
  { value: 'rejected',  label: 'Reject Business',    description: 'Does not meet requirements',       color: 'red',   icon: '✕' },
];

const TABS = ['Overview', 'Location', 'Operations', 'Metrics', 'History'];

// ─── API helpers ──────────────────────────────────────────────
const getToken = () => localStorage.getItem('accessToken');

const fetchBusiness = async (businessId) => {
  const res = await fetch(`${API_BASE}/api/admin/businesses/${businessId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Error ${res.status}`);
  }
  const json = await res.json();
  return json.data;
};

const fetchCategories = async () => {
  const res = await fetch(`${API_BASE}/api/categories`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data || [];
};

const fetchAuditLogs = async (businessId) => {
  const res = await fetch(`${API_BASE}/api/admin/businesses/${businessId}/audit-logs`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data || [];
};

const apiVerify = async ({ businessId, verificationStatus }) => {
  const res = await fetch(`${API_BASE}/api/admin/businesses/${businessId}/verify`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ verification_status: verificationStatus }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update verification');
  }
  return (await res.json()).data;
};

const apiPublish = async ({ businessId, isPublished }) => {
  const res = await fetch(`${API_BASE}/api/admin/businesses/${businessId}/publish`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ is_published: isPublished }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update publish status');
  }
  return (await res.json()).data;
};

// ─── Sub-components ───────────────────────────────────────────

/**
 * Status badge pill
 */
const Badge = ({ color, children }) => {
  const styles = {
    green:  'bg-green-100 text-green-700 ring-green-200',
    blue:   'bg-blue-100 text-blue-700 ring-blue-200',
    amber:  'bg-amber-100 text-amber-700 ring-amber-200',
    red:    'bg-red-100 text-red-700 ring-red-200',
    slate:  'bg-slate-100 text-slate-600 ring-slate-200',
    violet: 'bg-violet-100 text-violet-700 ring-violet-200',
    gold:   'bg-yellow-100 text-yellow-700 ring-yellow-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${styles[color] || styles.slate}`}>
      {children}
    </span>
  );
};

/**
 * Single info row: label + value, handles long text with break-all
 */
const InfoRow = ({ label, value, url, mono, truncate }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-3 py-2.5 border-b border-slate-100 last:border-0 min-w-0">
      <span className="text-[12px] text-slate-400 font-medium w-36 shrink-0 pt-0.5">{label}</span>
      {url ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-[13px] text-blue-600 hover:underline min-w-0 ${truncate ? 'truncate' : 'break-all'}`}
        >
          {value}
        </a>
      ) : (
        <span className={`text-[13px] text-slate-800 min-w-0 ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : mono ? 'break-all' : 'break-words'}`}>
          {value}
        </span>
      )}
    </div>
  );
};

/**
 * Section card wrapper
 */
const Section = ({ title, subtitle, children, action }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
      <div>
        <p className="text-[13px] font-semibold text-slate-800">{title}</p>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

/**
 * Metric card
 */
const MetricCard = ({ label, value, sub, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 min-w-0">
    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide truncate">{label}</p>
    <p className={`text-[26px] font-bold mt-1 ${color || 'text-slate-900'}`}>
      {value ?? <span className="text-slate-300 text-[20px]">—</span>}
    </p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </div>
);

/**
 * Opening hours renderer
 */
const OpeningHours = ({ hours }) => {
  if (!hours || typeof hours !== 'object') return <p className="text-[13px] text-slate-400 italic">Not set</p>;
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  const formatTime = (day) => {
    const dayHours = hours[day];
    if (!dayHours) return 'Closed';
    if (dayHours.closed) return 'Closed';
    if (dayHours.open && dayHours.close) {
      return `${dayHours.open} - ${dayHours.close}`;
    }
    return 'Closed';
  };
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
      {days.map((day) => (
        <div key={day} className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-[12px] text-slate-500 capitalize font-medium w-24 shrink-0">{day.slice(0, 3)}</span>
          <span className={`text-[12px] min-w-0 truncate ${
            formatTime(day) === 'Closed' ? 'text-slate-400' : 'text-slate-800'
          }`}>
            {formatTime(day)}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Skeleton loader for detail page
 */
const SkeletonDetail = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-36 bg-slate-200 rounded-xl" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="h-48 bg-slate-200 rounded-xl" />
      <div className="lg:col-span-2 h-48 bg-slate-200 rounded-xl" />
    </div>
    <div className="h-64 bg-slate-200 rounded-xl" />
  </div>
);

/**
 * Confirmation modal for approval actions
 */
const ApprovalModal = ({ open, action, business, onConfirm, onCancel, isLoading }) => {
  if (!open || !action) return null;

  const config = VERIFY_STATUSES.find((s) => s.value === action) || {};
  const colorMap = {
    green: { bg: 'bg-green-600 hover:bg-green-700', badge: 'bg-green-100 text-green-700', icon: '✓' },
    amber: { bg: 'bg-amber-500 hover:bg-amber-600', badge: 'bg-amber-100 text-amber-700', icon: '⚠' },
    red:   { bg: 'bg-red-600 hover:bg-red-700',   badge: 'bg-red-100 text-red-700',     icon: '✕' },
  };
  const c = colorMap[config.color] || colorMap.green;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-5 border-b border-slate-100 ${action === 'rejected' ? 'bg-red-50' : action === 'pending' ? 'bg-amber-50' : 'bg-green-50'}`}>
          <p className="text-[16px] font-bold text-slate-900">{config.label}</p>
          <p className="text-[12px] text-slate-500 mt-0.5">{config.description}</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Business summary */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg min-w-0">
            {business?.business_logo_url ? (
              <img
                src={business.business_logo_url}
                alt="logo"
                className="w-10 h-10 rounded object-contain border border-slate-200 shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gradient-to-br from-slate-200 to-slate-300 shrink-0 flex items-center justify-center text-slate-500 font-bold text-[14px]">
                {business?.business_name?.[0]?.toUpperCase() || 'B'}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-slate-900 truncate">{business?.business_name}</p>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">{business?.city}{business?.country ? `, ${business.country}` : ''}</p>
            </div>
          </div>

          {/* Current status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-slate-500">Current Status</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold text-[11px] ${
                business?.verification_status === 'verified' ? 'bg-green-100 text-green-700' :
                business?.verification_status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>{business?.verification_status || 'pending'}</span>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-slate-500">New Status</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold text-[11px] ${c.badge}`}>{action}</span>
            </div>
          </div>

          {/* Warning for rejection */}
          {action === 'rejected' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-[12px] text-red-700">
              ⚠ Rejecting will mark this business as <strong>not verified</strong> and hide it from public listings until re-reviewed.
            </div>
          )}

          {/* Info for approval */}
          {action === 'verified' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-[12px] text-green-700">
              ✓ Approving will mark this business as <strong>verified</strong>. You can also publish it separately using the Publish button.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-[12px] font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-5 py-2 text-[12px] font-semibold text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${c.bg}`}
          >
            {isLoading && <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            Confirm {config.label}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Publish toggle modal
 */
const PublishModal = ({ open, willPublish, business, onConfirm, onCancel, isLoading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className={`px-6 py-5 border-b border-slate-100 ${willPublish ? 'bg-blue-50' : 'bg-slate-50'}`}>
          <p className="text-[16px] font-bold text-slate-900">{willPublish ? 'Publish Business' : 'Unpublish Business'}</p>
          <p className="text-[12px] text-slate-500 mt-0.5">
            {willPublish ? 'Make this business visible in the public directory' : 'Hide this business from the public directory'}
          </p>
        </div>
        <div className="px-6 py-5">
          <p className="text-[13px] text-slate-700">
            {willPublish
              ? `"${business?.business_name}" will be publicly visible to all users.`
              : `"${business?.business_name}" will be removed from the public directory.`}
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="px-4 py-2 text-[12px] font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-5 py-2 text-[12px] font-semibold text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${willPublish ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-600 hover:bg-slate-700'}`}
          >
            {isLoading && <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {willPublish ? 'Publish' : 'Unpublish'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Dropdown component ───────────────────────────────────────
const DropdownMenu = ({ label, colorClass, items, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${colorClass}`}
      >
        {label}
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1.5 overflow-hidden">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => { item.onClick(); setOpen(false); }}
              disabled={item.disabled}
              className={`w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors disabled:opacity-40 ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700'}`}
            >
              <span className="text-[14px] shrink-0 mt-0.5">{item.icon}</span>
              <div className="min-w-0">
                <p className="text-[12px] font-semibold">{item.label}</p>
                {item.sub && <p className="text-[11px] text-slate-400 mt-0.5">{item.sub}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Page Component ─────────────────────────────────────
const AdminBusinessDetailPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI state
  const [activeTab, setActiveTab] = useState('Overview');
  const [toast, setToast] = useState(null);
  const [approvalModal, setApprovalModal] = useState({ open: false, action: null });
  const [publishModal, setPublishModal] = useState({ open: false, willPublish: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // ─── Queries ───────────────────────────────────────────────
  const businessQuery = useQuery({
    queryKey: ['admin', 'business', businessId],
    queryFn: () => fetchBusiness(businessId),
    staleTime: 2 * 60 * 1000,   // 2 min — admin data doesn't need sub-second freshness
    gcTime: 10 * 60 * 1000,     // keep in cache 10 min
    refetchOnWindowFocus: false, // prevent stealth refetch on tab-switch
    enabled: !!businessId,
  });

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    staleTime: 5 * 60 * 1000,
  });

  const auditQuery = useQuery({
    queryKey: ['admin', 'business', businessId, 'audit'],
    queryFn: () => fetchAuditLogs(businessId),
    enabled: !!businessId && activeTab === 'History',
    staleTime: 30 * 1000,
  });

  const business = businessQuery.data;
  const categories = categoriesQuery.data || [];

  const getCategoryName = (id) => {
    if (!id) return '—';
    const cat = categories.find((c) => c.id === id);
    return cat ? cat.name : id;
  };

  // ─── Mutations ─────────────────────────────────────────────
  const verifyMutation = useMutation({
    mutationFn: apiVerify,
    onMutate: async ({ verificationStatus }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ['admin', 'business', businessId] });
      const previous = queryClient.getQueryData(['admin', 'business', businessId]);
      queryClient.setQueryData(['admin', 'business', businessId], (old) => ({
        ...old,
        verification_status: verificationStatus,
        is_verified: verificationStatus === 'verified',
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['admin', 'business', businessId], ctx.previous);
      showToast('Failed to update verification status', 'error');
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(['admin', 'business', businessId], data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      showToast(`Business ${vars.verificationStatus === 'verified' ? 'approved & verified' : vars.verificationStatus}`, 'success');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'business', businessId] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: apiPublish,
    onMutate: async ({ isPublished }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'business', businessId] });
      const previous = queryClient.getQueryData(['admin', 'business', businessId]);
      queryClient.setQueryData(['admin', 'business', businessId], (old) => ({
        ...old,
        is_published: isPublished,
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['admin', 'business', businessId], ctx.previous);
      showToast('Failed to update publish status', 'error');
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      showToast(`Business ${vars.isPublished ? 'published' : 'unpublished'} successfully`, 'success');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'business', businessId] });
    },
  });

  // ─── Verify action handlers ────────────────────────────────
  const handleVerifyAction = (status) => {
    setApprovalModal({ open: true, action: status });
  };

  const confirmVerify = () => {
    verifyMutation.mutate(
      { businessId, verificationStatus: approvalModal.action },
      { onSettled: () => setApprovalModal({ open: false, action: null }) }
    );
  };

  const handlePublishAction = (willPublish) => {
    setPublishModal({ open: true, willPublish });
  };

  const confirmPublish = () => {
    publishMutation.mutate(
      { businessId, isPublished: publishModal.willPublish },
      { onSettled: () => setPublishModal({ open: false, willPublish: false }) }
    );
  };

  // ─── Helpers ───────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  const formatDateShort = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return '—'; }
  };

  const getVerificationColor = (status) => {
    if (status === 'verified') return 'green';
    if (status === 'rejected') return 'red';
    return 'amber';
  };

  // ─── Render ────────────────────────────────────────────────
  const isActionLoading = verifyMutation.isPending || publishMutation.isPending;

  if (businessQuery.isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-2xl text-red-500">✕</div>
          <p className="text-[15px] font-semibold text-slate-800">Failed to load business</p>
          <p className="text-[13px] text-slate-500">{businessQuery.error?.message}</p>
          <button
            onClick={() => businessQuery.refetch()}
            className="px-4 py-2 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Businesses', href: '/admin/businesses' },
    { label: business?.business_name || 'Loading…' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 p-6 max-w-[1400px]">

        {/* ── Toast ── */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* ── Approval Modals ── */}
        <ApprovalModal
          open={approvalModal.open}
          action={approvalModal.action}
          business={business}
          onConfirm={confirmVerify}
          onCancel={() => setApprovalModal({ open: false, action: null })}
          isLoading={verifyMutation.isPending}
        />
        <PublishModal
          open={publishModal.open}
          willPublish={publishModal.willPublish}
          business={business}
          onConfirm={confirmPublish}
          onCancel={() => setPublishModal({ open: false, willPublish: false })}
          isLoading={publishMutation.isPending}
        />

        {/* ── Breadcrumb ── */}
        <Breadcrumb items={breadcrumbItems} />

        {/* ── Page header row ── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/admin/businesses')}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors shrink-0"
              title="Back to listing"
            >
              <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-[20px] font-bold text-slate-900 truncate">
                {businessQuery.isLoading ? (
                  <span className="block h-6 w-48 bg-slate-200 rounded animate-pulse" />
                ) : (
                  business?.business_name || 'Business Detail'
                )}
              </h1>
              <p className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>ID: <span className="font-mono text-slate-500 select-all">{businessId}</span></span>
                {businessQuery.isFetching && !businessQuery.isLoading && (
                  <span className="inline-flex items-center gap-1 text-blue-500">
                    <span className="w-2 h-2 rounded-full border border-blue-400 border-t-transparent animate-spin" />
                    syncing…
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* ── Action Buttons ── */}
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* Verify dropdown */}
            <DropdownMenu
              label={
                business?.verification_status === 'verified' ? '✓ Verified'
                : business?.verification_status === 'rejected' ? '✕ Rejected'
                : '◷ Pending'
              }
              colorClass={
                business?.verification_status === 'verified'
                  ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  : business?.verification_status === 'rejected'
                  ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                  : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
              }
              disabled={isActionLoading || businessQuery.isLoading || ['verified', 'rejected'].includes(business?.verification_status)}
              items={VERIFY_STATUSES.map((s) => ({
                icon: s.icon,
                label: s.label,
                sub: s.description,
                disabled: business?.verification_status === s.value,
                danger: s.value === 'rejected',
                onClick: () => handleVerifyAction(s.value),
              }))}
            />

            {/* Publish toggle - only visible once business is verified */}
            {business?.verification_status === 'verified' && (
            <button
              onClick={() => handlePublishAction(!business?.is_published)}
              disabled={isActionLoading || businessQuery.isLoading}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                business?.is_published
                  ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                  : 'bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100'
              }`}
            >
              {business?.is_published ? (
                <><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg> Published</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg> Unpublished</>
              )}
            </button>
            )}

            {/* View reviews */}
            <Link
              to={`/admin/businesses/${businessId}/reviews`}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Reviews {business?.total_reviews ? `(${business.total_reviews})` : ''}
            </Link>
          </div>
        </div>

        {businessQuery.isLoading ? (
          <SkeletonDetail />
        ) : business ? (
          <>
            {/* ── Banner / Hero Card ── */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Banner */}
              <div className="relative h-32 bg-gradient-to-r from-slate-700 to-slate-900 overflow-hidden">
                {business.business_banner_url && (
                  <img
                    src={business.business_banner_url}
                    alt="banner"
                    className="w-full h-full object-cover opacity-60"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>

              {/* Info row */}
              <div className="px-5 pb-5 pt-2 flex flex-col sm:flex-row sm:items-start gap-4 flex-wrap">
                {/* Logo */}
                <div className="relative z-10 shrink-0 -mt-8 w-16 h-16 rounded-xl border-2 border-white shadow-md overflow-hidden bg-white flex items-center justify-center">
                  {business.business_logo_url ? (
                    <img
                      src={business.business_logo_url}
                      alt="logo"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerText = business.business_name?.[0]?.toUpperCase() || 'B';
                      }}
                    />
                  ) : (
                    <span className="text-[22px] font-bold text-slate-400">
                      {business.business_name?.[0]?.toUpperCase() || 'B'}
                    </span>
                  )}
                </div>

                {/* Name + badges */}
                <div className="flex-1 min-w-0 mt-1">
                  <h2 className="text-[18px] font-bold text-slate-900 truncate">{business.business_name}</h2>
                  <p className="text-[12px] text-slate-500 mt-0.5 truncate">
                    {getCategoryName(business.business_category)}
                    {business.city ? ` • ${business.city}` : ''}
                    {business.country ? `, ${business.country}` : ''}
                  </p>
                  {/* Status badges */}
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    <Badge color={getVerificationColor(business.verification_status)}>
                      {business.verification_status === 'verified' ? '✓' : business.verification_status === 'rejected' ? '✕' : '◷'} {business.verification_status || 'pending'}
                    </Badge>
                    {/* {business.is_verified && <Badge color="green">Verified</Badge>} */}
                    {business.is_approved && <Badge color="green">Approved</Badge>}
                    {business.is_published ? <Badge color="blue">Published</Badge> : <Badge color="slate">Unpublished</Badge>}
                    {business.verified_badge && <Badge color="gold">★ Verified Badge</Badge>}
                    {business.featured_until && new Date(business.featured_until) > new Date() && (
                      <Badge color="violet">Featured</Badge>
                    )}
                    {business.is_open ? <Badge color="green">Open</Badge> : <Badge color="slate">Closed</Badge>}
                  </div>
                </div>

                {/* Rating snapshot */}
                {business.avg_rating && (
                  <div className="shrink-0 flex flex-col items-center bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                    <span className="text-[22px] font-bold text-amber-600">{parseFloat(business.avg_rating).toFixed(1)}</span>
                    <span className="text-[11px] text-amber-500">★★★★★</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">{business.total_reviews || 0} reviews</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="border-b border-slate-200">
              <nav className="flex gap-0 -mb-px overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-3 text-[13px] font-semibold border-b-2 whitespace-nowrap transition-colors ${
                      activeTab === tab
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {tab}
                    {tab === 'History' && auditQuery.isFetching && (
                      <span className="ml-1.5 w-2 h-2 inline-block border border-blue-400 border-t-transparent rounded-full animate-spin" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* ── Tab Content ── */}
            <div>

              {/* ═══ OVERVIEW TAB ═══ */}
              {activeTab === 'Overview' && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  {/* Basic identity */}
                  <Section title="Business Identity" subtitle="Core business details">
                    <InfoRow label="Business Name"    value={business.business_name} />
                    <InfoRow label="Reg. Number"      value={business.business_registration_number} mono />
                    <InfoRow label="Category"         value={getCategoryName(business.business_category)} />
                    <InfoRow label="Description"      value={business.business_description} />
                    <InfoRow label="Website"          value={business.website_url} url truncate />
                    <InfoRow label="Business Email"   value={business.email_business} />
                    <InfoRow label="Phone"            value={business.phone_number} />
                    <InfoRow label="Team Members"     value={business.team_member_count} />
                  </Section>

                  {/* Admin / status info */}
                  <Section title="Admin & Status" subtitle="Verification and publication state">
                    <InfoRow label="Verification"      value={business.verification_status} />
                    <InfoRow label="Verified"          value={business.is_verified ? 'Yes' : 'No'} />
                    <InfoRow label="Approved"          value={business.is_approved ? 'Yes' : 'No'} />
                    <InfoRow label="Published"         value={business.is_published ? 'Yes' : 'No'} />
                    <InfoRow label="Verified Badge"    value={business.verified_badge ? 'Granted' : 'Not granted'} />
                    <InfoRow label="Featured Until"    value={business.featured_until ? formatDateShort(business.featured_until) : null} />
                    <InfoRow label="Verification Date" value={business.verification_date ? formatDateShort(business.verification_date) : null} />
                    <InfoRow label="Created By"        value={business.created_by_type} />
                    <InfoRow label="Created At"        value={formatDate(business.created_at)} />
                    <InfoRow label="Updated At"        value={formatDate(business.updated_at)} />
                    <InfoRow label="User ID"           value={business.user_id} mono />
                    <InfoRow label="Business ID"       value={business.id}      mono />
                  </Section>

                  {/* Media */}
                  {(business.business_logo_url || business.business_banner_url) && (
                    <Section title="Media" subtitle="Logo and banner images">
                      <div className="space-y-4">
                        {business.business_logo_url && (
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Logo</p>
                            <div className="flex items-center gap-3 min-w-0">
                              <img
                                src={business.business_logo_url}
                                alt="Logo"
                                className="w-16 h-16 rounded-lg border border-slate-200 object-contain shrink-0 bg-slate-50"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <a href={business.business_logo_url} target="_blank" rel="noopener noreferrer"
                                className="text-[11px] text-blue-600 hover:underline break-all min-w-0">
                                {business.business_logo_url}
                              </a>
                            </div>
                          </div>
                        )}
                        {business.business_banner_url && (
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Banner</p>
                            <img
                              src={business.business_banner_url}
                              alt="Banner"
                              className="w-full h-24 rounded-lg border border-slate-200 object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <a href={business.business_banner_url} target="_blank" rel="noopener noreferrer"
                              className="text-[11px] text-blue-600 hover:underline break-all mt-1.5 block">
                              {business.business_banner_url}
                            </a>
                          </div>
                        )}
                      </div>
                    </Section>
                  )}

                  {/* Features */}
                  {/* <Section title="Feature Flags" subtitle="Enabled capabilities for this business">
                    <InfoRow label="Auto Response"        value={business.enable_auto_response ? 'Enabled' : 'Disabled'} />
                    <InfoRow label="AI Insights"          value={business.enable_ai_insights ? 'Enabled' : 'Disabled'} />
                    <InfoRow label="Sentiment Analysis"   value={business.enable_sentiment_analysis ? 'Enabled' : 'Disabled'} />
                    {business.auto_reply_message && (
                      <InfoRow label="Auto Reply Msg"    value={business.auto_reply_message} />
                    )}
                  </Section> */}
                </div>
              )}

              {/* ═══ LOCATION TAB ═══ */}
              {activeTab === 'Location' && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  <Section title="Address Details" subtitle="Physical location of the business">
                    <InfoRow label="Street Address" value={business.street_address} />
                    <InfoRow label="City"           value={business.city} />
                    <InfoRow label="State/Province" value={business.state_province} />
                    <InfoRow label="Postal Code"    value={business.postal_code} mono />
                    <InfoRow label="Country"        value={business.country} />
                  </Section>

                  {/* <Section title="Geo Coordinates" subtitle="Latitude and longitude for map integration">
                    {business.latitude && business.longitude ? (
                      <>
                        <InfoRow label="Latitude"  value={business.latitude?.toString()} mono />
                        <InfoRow label="Longitude" value={business.longitude?.toString()} mono />
                        <div className="mt-4">
                          <a
                            href={`https://www.google.com/maps?q=${business.latitude},${business.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Open in Google Maps
                          </a>
                        </div>
                      </>
                    ) : (
                      <p className="text-[13px] text-slate-400 italic py-2">No coordinates recorded</p>
                    )}
                  </Section> */}
                </div>
              )}

              {/* ═══ OPERATIONS TAB ═══ */}
              {activeTab === 'Operations' && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  <Section title="Business Hours" subtitle="Opening hours by day of the week">
                    <OpeningHours hours={business.opening_hours} />
                  </Section>

                  <Section title="Operational Status" subtitle="Live status and response settings">
                    <InfoRow label="Currently Open"   value={business.is_open ? 'Yes' : 'No'} />
                    <InfoRow label="Team Members"     value={business.team_member_count?.toString()} />
                    <InfoRow label="Response Rate"    value={business.response_rate ? `${parseFloat(business.response_rate).toFixed(0)}%` : null} />
                    <InfoRow label="Avg Response Time" value={business.response_time_hours ? `${business.response_time_hours}h` : null} />
                  </Section>

                  {/* <Section title="Auto Reply" subtitle="Automated response message to customers">
                    {business.auto_reply_message ? (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-700 whitespace-pre-wrap break-words">
                        {business.auto_reply_message}
                      </div>
                    ) : (
                      <p className="text-[13px] text-slate-400 italic">Auto reply not configured</p>
                    )}
                  </Section> */}

                  {/* <Section title="Feature Flags" subtitle="AI and automation capabilities">
                    <div className="space-y-2.5">
                      {[
                        { label: 'Auto Response', value: business.enable_auto_response },
                        { label: 'AI Insights',   value: business.enable_ai_insights },
                        { label: 'Sentiment Analysis', value: business.enable_sentiment_analysis },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                          <span className="text-[12px] text-slate-500">{label}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${value ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {value ? '✓ Enabled' : '— Disabled'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Section> */}
                </div>
              )}

              {/* ═══ METRICS TAB ═══ */}
              {activeTab === 'Metrics' && (
                <div className="space-y-5">
                  {/* Metric cards row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                      label="Total Reviews"
                      value={business.total_reviews ?? 0}
                      color="text-blue-600"
                    />
                    <MetricCard
                      label="Avg Rating"
                      value={business.avg_rating ? parseFloat(business.avg_rating).toFixed(2) : null}
                      sub="Out of 10.00"
                      color="text-amber-600"
                    />
                    <MetricCard
                      label="Response Rate"
                      value={business.response_rate ? `${parseFloat(business.response_rate).toFixed(0)}%` : null}
                      sub="Replied to reviews"
                      color="text-green-600"
                    />
                    <MetricCard
                      label="Avg Response"
                      value={business.response_time_hours ? `${business.response_time_hours}h` : null}
                      sub="Average response time"
                      color="text-violet-600"
                    />
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                    <Section title="Performance Summary" subtitle="Review and engagement metrics">
                      <InfoRow label="Total Reviews"        value={business.total_reviews?.toString()} />
                      <InfoRow label="Average Rating"       value={business.avg_rating ? parseFloat(business.avg_rating).toFixed(2) : null} />
                      <InfoRow label="Response Rate"        value={business.response_rate ? `${parseFloat(business.response_rate).toFixed(2)}%` : null} />
                      <InfoRow label="Avg Response Time"    value={business.response_time_hours ? `${business.response_time_hours} hours` : null} />
                    </Section>

                    <Section
                      title="Reviews"
                      subtitle="Quick access to review management"
                      action={
                        <Link
                          to={`/admin/businesses/${businessId}/reviews`}
                          className="px-3 py-1.5 text-[11px] font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          View All Reviews →
                        </Link>
                      }
                    >
                      <p className="text-[13px] text-slate-500">
                        This business has received <strong className="text-slate-800">{business.total_reviews || 0}</strong> reviews.
                        {business.avg_rating &&
                          ` Average rating is ${parseFloat(business.avg_rating).toFixed(1)} / 10.`}
                      </p>
                    </Section>
                  </div>
                </div>
              )}

              {/* ═══ HISTORY TAB ═══ */}
              {activeTab === 'History' && (
                <div className="space-y-5">
                  {/* Current status snapshot */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Section title="Verification Status">
                      <div className="flex flex-col gap-2">
                        <Badge color={getVerificationColor(business.verification_status)}>
                          {business.verification_status || 'pending'}
                        </Badge>
                        {business.verification_date && (
                          <p className="text-[12px] text-slate-400 mt-1">
                            Changed: {formatDate(business.verification_date)}
                          </p>
                        )}
                      </div>
                    </Section>
                    <Section title="Created">
                      <p className="text-[13px] text-slate-700">{formatDate(business.created_at)}</p>
                      <p className="text-[11px] text-slate-400 mt-1 capitalize">by {business.created_by_type || '—'}</p>
                    </Section>
                    <Section title="Last Updated">
                      <p className="text-[13px] text-slate-700">{formatDate(business.updated_at)}</p>
                    </Section>
                  </div>

                  {/* Audit log */}
                  {/* <Section
                    title="Audit Log"
                    subtitle="All admin actions on this business"
                  >
                    {auditQuery.isLoading ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                        ))}
                      </div>
                    ) : auditQuery.data && auditQuery.data.length > 0 ? (
                      <div className="space-y-0 divide-y divide-slate-100">
                        {auditQuery.data.map((log, idx) => (
                          <div key={idx} className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 min-w-0">
                            <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0 pt-0.5 font-mono">
                              {formatDate(log.created_at || log.timestamp)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-slate-700 capitalize">
                                {(log.action_type || log.action || '').replace(/_/g, ' ')}
                              </p>
                              {log.actor_id && (
                                <p className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">by {log.actor_id}</p>
                              )}
                              {log.metadata && typeof log.metadata === 'object' && (
                                <p className="text-[11px] text-slate-400 mt-0.5 break-words">
                                  {JSON.stringify(log.metadata)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[13px] text-slate-400 italic py-3">
                        {auditQuery.isError
                          ? 'Audit log endpoint not available'
                          : 'No audit history recorded for this business'}
                      </p>
                    )}
                  </Section> */}
                </div>
              )}

            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default AdminBusinessDetailPage;
